'use client';
// MultiOutcomeMarketDetails Component - For markets with multiple outcomes/probabilities
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { ComponentErrorBoundary, MarketCardIcon, MobileTradeModal, TradeModal } from '@/components';
import { CommentsSection, MarketRules } from '@/components';
import { colors } from '@/styles/theme';
import type { Market, Outcome } from '@/types';
import { isMarketResolved } from '@/utils/marketUtils';

interface MultiOutcomeMarketDetailsProps {
  market: Market;
}

interface TradeModalState {
  isOpen: boolean;
  outcome: Outcome | null;
  isBuyOrder: boolean; // true for Buy Yes, false for Buy No
}

const INITIAL_TRADE_MODAL_STATE: TradeModalState = {
  isOpen: false,
  outcome: null,
  isBuyOrder: true,
};

export function MultiOutcomeMarketDetails({ market }: MultiOutcomeMarketDetailsProps) {
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isResolved = isMarketResolved(market);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('Past');
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<string | null>(
    market.outcomes?.[0]?.id || market.outcomes?.[2]?.id || null,
  );
  const [timeLeft, setTimeLeft] = useState({ hours: 3, minutes: 16, seconds: 10 });
  const [tradeModalState, setTradeModalState] =
    useState<TradeModalState>(INITIAL_TRADE_MODAL_STATE);

  const selectedOutcome =
    market.outcomes?.find((o) => o.id === selectedOutcomeId) || market.outcomes?.[0];

  useEffect(() => {
    if (!market.endsAt) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const endTime = new Date(market.endsAt!).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft({ hours, minutes, seconds });
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [market.endsAt]);

  function handleBack() {
    router.back();
  }

  function handleOutcomeSelect(outcomeId: string) {
    setSelectedOutcomeId(outcomeId);
  }

  function handleBuyYesClick(outcome: Outcome) {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setSelectedOutcomeId(outcome.id || '');
      setTradeModalState({
        isOpen: true,
        outcome,
        isBuyOrder: true, // Buy Yes
      });
    };
  }

  function handleBuyNoClick(outcome: Outcome) {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setSelectedOutcomeId(outcome.id || '');
      setTradeModalState({
        isOpen: true,
        outcome,
        isBuyOrder: false, // Buy No
      });
    };
  }

  function handleTradeModalClose() {
    setTradeModalState(INITIAL_TRADE_MODAL_STATE);
  }

  function formatVolume(volume?: number) {
    if (!volume) return '$0';
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(1)}m`;
    }
    if (volume >= 1000) {
      return `$${(volume / 1000).toFixed(0)}k`;
    }
    return `$${volume.toFixed(0)}`;
  }

  function formatPrice(price?: number) {
    if (price === undefined || price === null) return '0.0¢';
    const cents = Math.round(price * 100);
    return `${cents.toFixed(1)}¢`;
  }

  function calculateProbability(outcome: Outcome) {
    if (!outcome.price) return '0%';
    return `${Math.round(outcome.price * 100)}%`;
  }

  const timeFilters = ['Past', '12 PM', '12 PM Jan 16', '12 PM Jan 17', '12 PM Jan 18', 'More v'];

  const resolutionDate = market.endsAt
    ? new Date(market.endsAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const totalVolume =
    market.outcomes?.reduce((sum, o) => sum + (o.volume || 0), 0) || market.volume || 0;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBack} size="small" sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <CodeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <BookmarkBorderIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary', width: 32, height: 32 }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Market Header Card */}
      <Card
        sx={{
          bgcolor: 'background.paper',
          border: 'none',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              {market.icon && <MarketCardIcon icon={market.icon} />}
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
              >
                {market.question || market.title}
              </Typography>
            </Box>
            {/* Countdown Timer */}
            <Typography
              variant="body2"
              sx={{
                color: '#e0664f',
                fontWeight: 700,
                fontSize: '0.875rem',
                bgcolor: '#fff',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              {String(timeLeft.hours).padStart(2, '0')} HRS{' '}
              {String(timeLeft.minutes).padStart(2, '0')} MINS{' '}
              {String(timeLeft.seconds).padStart(2, '0')} SECS
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {formatVolume(totalVolume)} Vol.
            </Typography>
            {resolutionDate && (
              <Typography variant="body2" color="text.secondary">
                {resolutionDate}
              </Typography>
            )}
          </Box>
          {/* Time Filters */}
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
            {timeFilters.map((filter) => (
              <Chip
                key={filter}
                label={filter}
                onClick={() => setSelectedTimeFilter(filter)}
                sx={{
                  bgcolor: selectedTimeFilter === filter ? colors.neutral[100] : 'transparent',
                  color: selectedTimeFilter === filter ? colors.neutral[900] : colors.neutral[500],
                  border: '1px solid',
                  borderColor: selectedTimeFilter === filter ? colors.neutral[300] : 'transparent',
                  fontWeight: selectedTimeFilter === filter ? 600 : 400,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: colors.neutral[50],
                  },
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* Left Column - Outcomes Table */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card
            sx={{
              bgcolor: 'background.paper',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          borderBottom: `2px solid ${colors.neutral[200]}`,
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        OUTCOME
                      </TableCell>
                      {!isResolved && (
                        <TableCell
                          align="right"
                          sx={{
                            py: 1.5,
                            px: 2,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          % CHANCE
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {market.outcomes?.map((outcome) => {
                      const probability = calculateProbability(outcome);
                      const yesPrice = formatPrice(outcome.price);
                      const noPrice = formatPrice(outcome.price ? 1 - outcome.price : 0);
                      const isSelected = selectedOutcomeId === outcome.id;

                      return (
                        <TableRow
                          key={outcome.id}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: colors.neutral[50],
                            },
                            bgcolor: isSelected ? colors.neutral[100] : 'transparent',
                          }}
                          onClick={() => handleOutcomeSelect(outcome.id || '')}
                        >
                          <TableCell sx={{ py: 1.5, px: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{ fontSize: '0.9375rem' }}
                              >
                                {outcome.name || outcome.id}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                {formatVolume(outcome.volume)} Vol.
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 2 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ fontSize: '0.9375rem' }}
                                >
                                  {probability}
                                </Typography>
                                {/* Change indicator - placeholder for now */}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleBuyYesClick(outcome)}
                                  sx={{
                                    bgcolor: '#4ec38a',
                                    color: '#fff',
                                    minWidth: 100,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    px: 1.5,
                                    py: 0.75,
                                    boxShadow: 'none',
                                    '&:hover': {
                                      bgcolor: '#1f9d63',
                                      boxShadow: 'none',
                                    },
                                  }}
                                >
                                  Buy Yes {yesPrice}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleBuyNoClick(outcome)}
                                  sx={{
                                    bgcolor: '#e0664f',
                                    color: '#fff',
                                    minWidth: 100,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    px: 1.5,
                                    py: 0.75,
                                    boxShadow: 'none',
                                    '&:hover': {
                                      bgcolor: '#c24632',
                                      boxShadow: 'none',
                                    },
                                  }}
                                >
                                  Buy No {noPrice}
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Market Rules Section */}
          <ComponentErrorBoundary componentName="Market Rules">
            <MarketRules
              marketId={market.id || ''}
              description={market.description || 'No governing rules provided.'}
            />
          </ComponentErrorBoundary>

          {/* Comments Section */}
          <ComponentErrorBoundary componentName="Comments Section">
            <CommentsSection marketId={market.id || ''} />
          </ComponentErrorBoundary>
        </Box>
      </Box>

      {/* Trade Modal - Desktop */}
      {tradeModalState.outcome && isDesktop && (
        <TradeModal
          isOpen={tradeModalState.isOpen}
          market={market}
          outcome={tradeModalState.outcome}
          isBuyOrder={tradeModalState.isBuyOrder}
          onClose={handleTradeModalClose}
        />
      )}

      {/* Trade Modal - Mobile */}
      {tradeModalState.outcome && !isDesktop && (
        <MobileTradeModal
          isOpen={tradeModalState.isOpen}
          market={market as any}
          outcome={tradeModalState.outcome as any}
          isBuyOrder={tradeModalState.isBuyOrder}
          onClose={handleTradeModalClose}
        />
      )}
    </Box>
  );
}

export default MultiOutcomeMarketDetails;
